import { DataEntry } from 'models/data';
import { Tile } from 'components/Layout/PageContainer';
import { ReactNode, useMemo } from 'react';
import { ExpansionOpportunityCard } from 'pages/Benchmarking/common/expansion-opportunities/ExpansionOpportunityCard';
import { MetricsSectionTitle } from 'components/MetricsSectionTitle';
import { useIntl } from 'react-intl';
import { Typography } from '@mui/material';
import { NoOpportunitiesFound } from 'pages/Benchmarking/common/expansion-opportunities/NoOpportunitiesFound';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { TbIcon } from 'components/Figma';
import { Carousel } from 'components/Carousel';

export interface ExpansionOpportunitiesProps {
  expansionOpportunities: DataEntry[];
  benchmark: string;
  isPending?: boolean;
}

export function ExpansionOpportunities({
  expansionOpportunities,
  benchmark,
  isPending = false,
}: ExpansionOpportunitiesProps) {
  const intl = useIntl();

  const sortedAndLimitedOpportunities = useMemo(() => {
    const sorted = [...expansionOpportunities].sort((a, b) => {
      if (a.benchmarks && b.benchmarks) {
        const growthA = a.benchmarks[benchmark]?.values?.growth ?? 0;
        const growthB = b.benchmarks[benchmark]?.values?.growth ?? 0;
        return growthB - growthA;
      }
      return 0;
    });
    return sorted
      .filter(opp => {
        return (opp.benchmarks ? (opp.benchmarks[benchmark]?.values?.growth ?? 0) : 0) > 1;
      })
      .slice(0, 20);
  }, [expansionOpportunities, benchmark]);

  const carouselItems = useMemo(() => {
    const displaySlices: ReactNode[] = [];

    for (let i = 0; i < sortedAndLimitedOpportunities.length; i = i + 1) {
      const displaySlice = (
        <div key={`slice-${i}`} className="flex gap-[30px] justify-center w-full">
          <ExpansionOpportunityCard
            key={`${i}-0`}
            expansionOpportunity={sortedAndLimitedOpportunities[i]}
            benchmark={benchmark}
          />
        </div>
      );
      displaySlices.push(displaySlice);
    }

    return displaySlices;
  }, [sortedAndLimitedOpportunities, benchmark]);

  const renderCarousel = () => {
    return (
      <>
        <Carousel list={carouselItems} />

        <div className="flex gap-[18px] items-center pt-[30px]">
          <TbIcon name="InformationOutlined" size="xl" />
          <Typography fontSize="14px" fontWeight={300}>
            {intl.formatMessage({ id: 'benchmarking.info.expansion-opportunities' })}
          </Typography>
        </div>
      </>
    );
  };

  return (
    <Tile>
      {isPending ? (
        <MetricsLoadingSpinner />
      ) : (
        <>
          <MetricsSectionTitle
            additionalTitle={intl.formatMessage({
              id: 'benchmarking.titles.expansion-opportunities.subtitle',
              defaultMessage: 'by Peer Growth',
            })}
          >
            {intl.formatMessage({
              id: 'benchmarking.titles.expansion-opportunities.title',
              defaultMessage: 'Expansion Opportunities',
            })}
          </MetricsSectionTitle>
          {sortedAndLimitedOpportunities?.length === 0 ? <NoOpportunitiesFound /> : renderCarousel()}
        </>
      )}
    </Tile>
  );
}
